import React, { useState, useEffect, useRef } from 'react';
import './ProfileSettings.css';
import {
  HelpCircle,
  ChevronDown,
  Upload,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import { onAuthStateChanged, signOut, updateProfile } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import UserDashboardSidebar from './UserDashboardSidebar';

// Local Storage Helper
const LocalStorageHelper = {
  saveLoginData: (userId, email, name, userType = 'user') => {
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userName', name);
    localStorage.setItem('userType', userType);
  },

  isLoggedIn: () => {
    return localStorage.getItem('isLoggedIn') === 'true';
  },

  getLoginData: () => {
    return {
      userId: localStorage.getItem('userId'),
      email: localStorage.getItem('userEmail'),
      name: localStorage.getItem('userName'),
      userType: localStorage.getItem('userType'),
    };
  },

  clearLoginData: () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
  }
};

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [saving, setSaving] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    profileImage: '',
    dateOfBirth: '',
    state: '',
    city: '',
    address: '',
    language: 'English'
  });
  const [avatarUrl, setAvatarUrl] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  // Authentication state for sidebar
  const [userData, setUserData] = useState(null);
  const [userName, setUserName] = useState('User');
  const [profilePhoto, setProfilePhoto] = useState(null);

  // Generate avatar URL helper
  const generateAvatarUrl = (userData, user) => {
    const avatarSources = [
      userData?.profileImage,
      userData?.avatarUrl,
      userData?.avatar,
      user?.photoURL
    ];
    
    const validAvatar = avatarSources.find(url => url && url.trim() !== '' && url !== 'undefined');
    
    if (validAvatar) {
      return validAvatar;
    }
    
    const name = userData?.name || user?.displayName || "User";
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
  };

  // Compress image to under 200KB
  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.2, // Maximum 200KB
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: file.type,
    };

    try {
      console.log(`Original file size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
      const compressedFile = await imageCompression(file, options);
      console.log(`Compressed file size: ${(compressedFile.size / 1024).toFixed(2)} KB`);
      return compressedFile;
    } catch (error) {
      console.error('Image compression error:', error);
      throw new Error('Failed to compress image');
    }
  };

  // UPDATED: Handle file upload for avatar with compression - using profile_photos folder
  const handleFileUpload = async (file) => {
    if (!file || !userProfile) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    try {
      setUploadingAvatar(true);
      
      // Compress image first
      const compressedFile = await compressImage(file);
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `profile_${userProfile.id}_${timestamp}.${fileExtension}`;
      
      // UPDATED: Create storage reference to profile_photos folder
      const storageRef = ref(storage, `profile_photos/User/${fileName}`);
      
      // Upload compressed file
      console.log('Uploading compressed file to Firebase Storage profile_photos folder...');
      const snapshot = await uploadBytes(storageRef, compressedFile);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('File uploaded successfully to profile_photos. Download URL:', downloadURL);
      
      // Update profile data with new image URL
      setProfileData(prev => ({
        ...prev,
        profileImage: downloadURL
      }));
      
      setAvatarUrl(downloadURL);
      setProfilePhoto(downloadURL);
      
      // Auto-save the profile image to Firebase
      await updateProfileImage(downloadURL);
      
      alert('Profile picture updated successfully! 📸');
      
    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert(error.message || 'Failed to upload profile picture. Please try again.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Update profile image in Firebase
  const updateProfileImage = async (imageUrl) => {
    try {
      const userType = userProfile.userType || 'user';
      const userDocRef = doc(db, userType === 'vendor' ? 'photographers' : 'users', userProfile.id);
      
      await updateDoc(userDocRef, {
        profileImage: imageUrl,
        updatedAt: new Date()
      });
      
      console.log('Profile image updated in Firestore');
    } catch (error) {
      console.error('Error updating profile image in Firestore:', error);
    }
  };

  // Handle avatar update with file picker
  const handleUpdateAvatar = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file input change
  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset the input value so the same file can be selected again
    event.target.value = '';
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      LocalStorageHelper.clearLoginData();
      navigate('/Login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Handle home navigation
  const handleHomeNavigation = () => {
    navigate('/LandingPage');
    closeSidebar();
  };

  // Enhanced Firebase Authentication Check - Load data in background
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Set basic user data immediately for UI display
          const loginData = LocalStorageHelper.getLoginData();
          let userType = loginData.userType || 'user';
          
          // Set basic profile data immediately
          const basicProfile = {
            id: user.uid,
            name: user.displayName || loginData.userName || "User",
            email: user.email || loginData.userEmail,
            userType: userType,
            avatar: generateAvatarUrl({}, user),
            isVerified: user.emailVerified || false
          };
          
          setUserProfile(basicProfile);
          setAvatarUrl(basicProfile.avatar);
          setUserName(basicProfile.name);
          
          // Set basic form data immediately
          const basicFormData = {
            name: basicProfile.name,
            email: basicProfile.email,
            phone: '',
            location: '',
            profileImage: '',
            dateOfBirth: '',
            state: '',
            city: '',
            address: '',
            language: 'English'
          };
          
          setProfileData(basicFormData);
          
          // Now load detailed data from Firebase in background
          // console.log('Loading detailed data from Firebase...');
          
          // Try to get user data from the expected collection first
          let userDocRef = doc(db, userType === 'vendor' ? 'photographers' : 'users', user.uid);
          let userDoc = await getDoc(userDocRef);
          
          // If not found in expected collection, try the other collection
          if (!userDoc.exists()) {
            console.log('User not found in expected collection, trying alternative...');
            userType = userType === 'vendor' ? 'user' : 'vendor';
            userDocRef = doc(db, userType === 'vendor' ? 'photographers' : 'users', user.uid);
            userDoc = await getDoc(userDocRef);
            
            // Update localStorage with correct user type
            if (userDoc.exists()) {
              LocalStorageHelper.saveLoginData(
                user.uid,
                user.email,
                user.displayName || "User",
                userType
              );
            }
          }
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('Detailed user data loaded from Firestore:', userData);
            
            // Generate avatar URL
            const avatar = generateAvatarUrl(userData, user);
            
            // Set detailed user profile
            const detailedProfile = {
              id: user.uid,
              name: userData.name || user.displayName || "User",
              email: userData.email || user.email,
              userType: userType,
              avatar: avatar,
              isVerified: user.emailVerified || userData.emailVerified || false
            };
            
            setUserProfile(detailedProfile);
            setAvatarUrl(avatar);
            
            // Set sidebar user data
            setUserData(userData);
            setUserName(userData.name || user.displayName || 'User');
            setProfilePhoto(userData.profileImage || userData.avatar || null);
            
            // Set detailed profile form data
            const detailedFormData = {
              name: userData.name || user.displayName || '',
              email: userData.email || user.email || '',
              phone: userData.phone || '',
              location: userData.location || '',
              profileImage: userData.profileImage || '',
              dateOfBirth: userData.dateOfBirth || '',
              state: userData.state || '',
              city: userData.city || '',
              address: userData.address || '',
              language: userData.language || 'English'
            };
            
            setProfileData(detailedFormData);
            
            // Update localStorage with fresh data
            LocalStorageHelper.saveLoginData(
              user.uid,
              userData.email || user.email,
              userData.name || user.displayName || "User",
              userType
            );
            
          } else {
            console.log('User document not found in either collection');
            // Keep basic profile data from auth user
            setUserData({});
            setProfilePhoto(null);
          }
          
          setDataLoaded(true);
          console.log('Profile data loaded successfully');
          
        } catch (error) {
          console.error('Error fetching user data:', error);
          setDataLoaded(true); // Still set to true to show the form
        }
      } else {
        // User is not logged in
        console.log('No user logged in, redirecting to login');
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && !sidebarVisible) {
        setSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarVisible]);

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Enhanced Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userProfile) {
      alert('User profile not loaded. Please refresh the page.');
      return;
    }
    
    try {
      setSaving(true);
      
      // Get user type from current userProfile state
      const userType = userProfile.userType || 'user';
      
      // Prepare data to update
      const updateData = {
        name: profileData.name.trim(),
        phone: profileData.phone.trim(),
        location: profileData.location.trim(),
        profileImage: profileData.profileImage.trim(),
        language: profileData.language,
        dateOfBirth: profileData.dateOfBirth,
        state: profileData.state,
        city: profileData.city,
        address: profileData.address.trim(),
        updatedAt: new Date(),
        email: profileData.email
      };

      // Remove empty strings except for required fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '' && !['email', 'updatedAt', 'name'].includes(key)) {
          delete updateData[key];
        }
      });
      
      // Update user data in Firestore
      const userDocRef = doc(db, userType === 'vendor' ? 'photographers' : 'users', userProfile.id);
      await updateDoc(userDocRef, updateData);
      
      // Update Firebase Auth profile if name changed
      if (profileData.name !== userProfile.name && auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: profileData.name
        });
      }
      
      // Update localStorage
      LocalStorageHelper.saveLoginData(
        userProfile.id,
        profileData.email,
        profileData.name,
        userType
      );
      
      // Update local state
      setUserProfile(prev => ({
        ...prev,
        name: profileData.name
      }));
      
      // Update sidebar user data
      setUserName(profileData.name);
      if (profileData.profileImage) {
        setProfilePhoto(profileData.profileImage);
      }
      
      alert('Profile updated successfully! 🎉');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('⚠️ Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data.')) {
      try {
        console.log('Account deletion requested for user:', userProfile?.id);
        alert('Account deletion feature will be implemented soon. Please contact support for assistance.');
      } catch (error) {
        console.error('Error deleting account:', error);
        alert('Failed to delete account. Please contact support for assistance.');
      }
    }
  };

  // UPDATED: Remove avatar function - also handles profile_photos folder
  const handleRemoveAvatar = async () => {
    if (window.confirm('Are you sure you want to remove your profile picture?')) {
      try {
        setUploadingAvatar(true);
        
        // If there's a current profile image in Firebase Storage, delete it
        if (profileData.profileImage && profileData.profileImage.includes('firebase')) {
          try {
            // Handle both old profile-images and new profile_photos paths
            let imageRef;
            if (profileData.profileImage.includes('profile_photos')) {
              imageRef = ref(storage, profileData.profileImage);
            } else if (profileData.profileImage.includes('profile-images')) {
              imageRef = ref(storage, profileData.profileImage);
            } else {
              // Extract filename and assume it's in profile_photos
              const urlParts = profileData.profileImage.split('/');
              const filename = urlParts[urlParts.length - 1].split('?')[0];
              imageRef = ref(storage, `profile_photos/${filename}`);
            }
            
            await deleteObject(imageRef);
            console.log('Previous profile image deleted from storage');
          } catch (deleteError) {
            console.log('Could not delete previous image:', deleteError);
          }
        }
        
        // Update profile data
        setProfileData(prev => ({
          ...prev,
          profileImage: ''
        }));
        
        // Generate fallback avatar
        const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData.name || 'User')}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
        setAvatarUrl(fallbackAvatar);
        setProfilePhoto(null);
        
        // Update in Firebase
        await updateProfileImage('');
        
        alert('Profile picture removed successfully!');
        
      } catch (error) {
        console.error('Error removing avatar:', error);
        alert('Failed to remove profile picture. Please try again.');
      } finally {
        setUploadingAvatar(false);
      }
    }
  };

  // Calculate profile completion percentage
  const getProfileCompletion = () => {
    const fields = ['name', 'email', 'phone', 'location', 'state', 'city'];
    const filledFields = fields.filter(field => profileData[field] && profileData[field].trim() !== '');
    return Math.round((filledFields.length / fields.length) * 100);
  };

  return (
    <div className="usettings-container">
      {/* Hidden file input for avatar upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept="image/*"
        style={{ display: 'none' }}
      />

      <UserDashboardSidebar
        activeItem="settings"
        visible={sidebarVisible}
        isMobile={isMobile}
        onToggle={toggleSidebar}
        onClose={closeSidebar}
        userName={userName}
        profilePhoto={profilePhoto}
        onHome={handleHomeNavigation}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <main className={`usettings-main-content ${
        !sidebarVisible && !isMobile ? 'expanded' : ''
      }`}>
        <div className="usettings-content-wrap">
        {/* Show loading indicator only for data that's still loading */}
        {!dataLoaded && (
          <div className="usettings-loading-indicator">
            <div className="usettings-loading-spinner"></div>
            <span>Loading profile data from Firebase...</span>
          </div>
        )}

        <div className="usettings-layout">
        <form className="usettings-form" onSubmit={handleSubmit}>
          {/* Profile header with file upload */}
          <div className="usettings-header">
            <div className="usettings-avatar-container">
              <div className="usettings-avatar-large">
                {uploadingAvatar ? (
                  <div className="usettings-avatar-uploading">
                    <div className="usettings-loading-spinner usettings-loading-spinner-sm"></div>
                  </div>
                ) : (
                  <img
                    src={avatarUrl}
                    alt={userProfile?.name || 'User'}
                    className="usettings-avatar-img"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.name || 'User')}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
                    }}
                  />
                )}
              </div>
            </div>
            <div className="usettings-actions">
              <button 
                type="button" 
                className="usettings-btn-outline" 
                onClick={handleUpdateAvatar}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? (
                  <>
                    <div className="usettings-loading-spinner usettings-loading-spinner-xs"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="usettings-btn-icon-svg" />
                    Upload Photo
                  </>
                )}
              </button>
              <button 
                type="button" 
                className="usettings-btn-icon" 
                onClick={handleRemoveAvatar}
                disabled={uploadingAvatar}
              >
                <span>Remove</span>
              </button>
            </div>
          </div>

          <div className="usettings-form-group">
            <label>
              Name *
              <HelpCircle size={16} className="usettings-help-icon" />
            </label>
            <input
              type="text"
              name="name"
              value={profileData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />
          </div>

          <div className="usettings-form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={profileData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              disabled
              className="usettings-input-disabled"
            />
            <small className="usettings-field-hint">
              Email cannot be changed
            </small>
          </div>

          <div className="usettings-form-group">
            <label>Phone</label>
            <input
              type="tel"
              name="phone"
              value={profileData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
            />
          </div>

          <div className="usettings-form-group">
            <label>Location</label>
            <input
              type="text"
              name="location"
              value={profileData.location}
              onChange={handleChange}
              placeholder="Enter your location"
            />
          </div>

          <div className="usettings-form-group">
            <label>Language</label>
            <div className="usettings-select-wrapper">
              <select
                name="language"
                value={profileData.language}
                onChange={handleChange}
              >
                <option value="English">English</option>
                <option value="Hindi">Hindi</option>
                <option value="Marathi">Marathi</option>
                <option value="Gujarati">Gujarati</option>
                <option value="Tamil">Tamil</option>
                <option value="Telugu">Telugu</option>
              </select>
              <ChevronDown size={16} className="usettings-select-arrow" />
            </div>
          </div>

          <div className="usettings-form-group">
            <label>
              Date of Birth
              <HelpCircle size={16} className="usettings-help-icon" />
            </label>
            <input
              type="date"
              name="dateOfBirth"
              value={profileData.dateOfBirth}
              onChange={handleChange}
            />
          </div>

          <div className="usettings-form-group">
            <label>State</label>
            <div className="usettings-select-wrapper">
              <select
                name="state"
                value={profileData.state}
                onChange={handleChange}
              >
                <option value="">Select State</option>
                <option value="Maharashtra">Maharashtra</option>
                <option value="Delhi">Delhi</option>
                <option value="Karnataka">Karnataka</option>
                <option value="Tamil Nadu">Tamil Nadu</option>
                <option value="Gujarat">Gujarat</option>
                <option value="Rajasthan">Rajasthan</option>
                <option value="West Bengal">West Bengal</option>
                <option value="Uttar Pradesh">Uttar Pradesh</option>
                <option value="Punjab">Punjab</option>
                <option value="Haryana">Haryana</option>
              </select>
              <ChevronDown size={16} className="usettings-select-arrow" />
            </div>
          </div>

          <div className="usettings-form-group">
            <label>City</label>
            <div className="usettings-select-wrapper">
              <select
                name="city"
                value={profileData.city}
                onChange={handleChange}
              >
                <option value="">Select City</option>
                <option value="Mumbai">Mumbai</option>
                <option value="Pune">Pune</option>
                <option value="Nagpur">Nagpur</option>
                <option value="Delhi">Delhi</option>
                <option value="Bangalore">Bangalore</option>
                <option value="Chennai">Chennai</option>
                <option value="Kolkata">Kolkata</option>
                <option value="Hyderabad">Hyderabad</option>
                <option value="Ahmedabad">Ahmedabad</option>
                <option value="Jaipur">Jaipur</option>
              </select>
              <ChevronDown size={16} className="usettings-select-arrow" />
            </div>
          </div>

          <div className="usettings-form-group">
            <label>Address</label>
            <textarea
              name="address"
              value={profileData.address}
              onChange={handleChange}
              placeholder="Enter your complete address"
              rows="3"
              className="usettings-textarea"
            />
          </div>

          <div className="usettings-form-actions">
            <button type="submit" className="usettings-btn-primary" disabled={saving || uploadingAvatar}>
              {saving ? ' Saving...' : ' Save Changes'}
            </button>
            <button type="button" className="usettings-btn-outline" onClick={() => {
              navigate(-1);
            }}>
              Cancel
            </button>
            <button type="button" className="usettings-btn-delete" onClick={handleDelete}>
               Delete Account
            </button>
          </div>
        </form>

        {/* Profile summary / preview column */}
        <aside className="usettings-summary">
          <div className="usettings-summary-card">
            <div className="usettings-summary-avatar">
              <img
                src={avatarUrl}
                alt={userProfile?.name || 'User'}
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.name || 'User')}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
                }}
              />
            </div>
            <div className="usettings-summary-name">{profileData.name || userName}</div>
            <div className="usettings-summary-email">{profileData.email}</div>
            <div className={`usettings-summary-badge ${userProfile?.userType === 'vendor' ? 'is-pro' : ''}`}>
              {userProfile?.userType === 'vendor' ? 'Pro Account' : 'Member'}
            </div>
          </div>

          <div className="usettings-summary-stats">
            <div className="usettings-stat-row">
              <span className="usettings-stat-row-label">Email status</span>
              <span className={`usettings-stat-row-value ${userProfile?.isVerified ? 'is-good' : 'is-pending'}`}>
                {userProfile?.isVerified ? 'Verified' : 'Pending'}
              </span>
            </div>
            <div className="usettings-stat-row">
              <span className="usettings-stat-row-label">Profile complete</span>
              <span className="usettings-stat-row-value">{getProfileCompletion()}%</span>
            </div>
            <div className="usettings-progress-track">
              <div className="usettings-progress-fill" style={{ width: `${getProfileCompletion()}%` }}></div>
            </div>
          </div>

          <div className="usettings-summary-tip">
            Fill in your city, state and phone number so photographers can match you with the right service.
          </div>
        </aside>
        </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileSettings;
